import { Check, CircleNotch, Sparkle, Warning, Circle } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const STATUS_ICON = {
    completed: <Check size={14} weight="bold" className="text-emerald-700" />,
    processing: <CircleNotch size={14} weight="bold" className="text-amber-700 animate-spin" />,
    failed: <Warning size={14} weight="bold" className="text-destructive" />,
    queued: <Circle size={10} weight="duotone" className="text-muted-foreground" />,
};

const STATUS_RING = {
    completed: "bg-emerald-100 border-emerald-400",
    processing: "bg-amber-100 border-amber-400 ring-2 ring-amber-300/50",
    failed: "bg-red-100 border-red-400",
    queued: "bg-muted border-border",
};

const STATUS_TEXT = {
    completed: "text-foreground",
    processing: "text-amber-900 font-semibold",
    failed: "text-destructive font-semibold",
    queued: "text-muted-foreground",
};

/**
 * Renders a vertical timeline of async-job stages with live reasoning text.
 * Pass `stages` from the backend job document. Each stage has:
 *   { key, label, status: queued|processing|completed|failed, reasoning?: string }
 *
 * Use `compact` to render a slim one-line-per-stage variant for non-LLM jobs
 * (e.g., document upload) where stage text isn't worth showing.
 */
export function ProgressTimeline({
    stages = [],
    title = "Progress",
    subtitle,
    compact = false,
    "data-testid": testid = "progress-timeline",
}) {
    if (!stages.length) return null;
    const allDone = stages.every((s) => s.status === "completed");
    const anyFailed = stages.some((s) => s.status === "failed");
    const inFlight = !allDone && !anyFailed;

    return (
        <div
            className="border border-border rounded-md bg-white p-4 shadow-sm"
            data-testid={testid}
        >
            <div className="flex items-center gap-2 mb-3">
                <Sparkle
                    size={15}
                    weight="fill"
                    className={inFlight ? "text-amber-600 animate-pulse" : anyFailed ? "text-destructive" : "text-emerald-700"}
                />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    {title}
                </div>
                {subtitle && (
                    <div className="ml-auto text-xs text-muted-foreground">{subtitle}</div>
                )}
            </div>
            <ol className="relative space-y-1">
                {stages.map((s, idx) => {
                    const isLast = idx === stages.length - 1;
                    return (
                        <li
                            key={s.key}
                            className="flex gap-3"
                            data-testid={`stage-${s.key}`}
                            data-status={s.status}
                        >
                            <div className="flex flex-col items-center pt-1">
                                <div
                                    className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${
                                        STATUS_RING[s.status] || STATUS_RING.queued
                                    }`}
                                >
                                    {STATUS_ICON[s.status] || STATUS_ICON.queued}
                                </div>
                                {!isLast && <div className="w-px flex-1 bg-border my-1" />}
                            </div>
                            <div className="flex-1 pb-3">
                                <div className={`text-sm ${STATUS_TEXT[s.status] || ""}`}>
                                    {s.label}
                                    {s.status === "processing" && (
                                        <span className="ml-2 text-xs text-amber-700/70">working…</span>
                                    )}
                                </div>
                                {!compact && s.reasoning && s.status !== "queued" && (
                                    <div
                                        className={`text-xs mt-1 leading-relaxed border-l-2 pl-3 py-1.5 max-h-72 overflow-y-auto ${
                                            s.status === "failed"
                                                ? "border-destructive bg-red-50/60 text-destructive"
                                                : "border-amber-300 bg-amber-50/40 text-foreground/90"
                                        }`}
                                        data-testid={`stage-reasoning-${s.key}`}
                                    >
                                        <article className="prose prose-xs max-w-none [&_*]:text-xs [&_*]:leading-relaxed [&_p]:mb-1 [&_ol]:mt-0.5 [&_ol]:mb-0.5 [&_ul]:mt-0.5 [&_ul]:mb-0.5 [&_strong]:font-semibold">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {s.reasoning}
                                            </ReactMarkdown>
                                        </article>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
